import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Modal, Paper, Fade, IconButton, Box, TextField, InputAdornment, Tooltip, Button } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { URL } from "../config";
import AddProductModal from "./AddProducts"; 
import toast from "react-hot-toast";
import { useOutletContext } from "react-router-dom";
import { Form } from "antd";

const ProductList = () => {
  const [form] = Form.useForm();

  const { setLoading } = useOutletContext();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState([]);
  const [services, setServices] = useState([]);
  const [brands, setBrands] = useState([]);
  const [intercoms, setIntercoms] = useState([]);
  const [wireds, setWireds] = useState([]);
  const [cameraTypes, setCameraTypes] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const normalize = (res) =>
  Array.isArray(res.data) ? res.data : res.data?.data || [];

const fetchAll = async () => {
  setLoading(true);
  try {
    const [
      servicesRes,
      brandsRes,
      intercomRes,
      wiredRes,
      productsRes,
      cameraTypesRes,
    ] = await Promise.all([
      axios.get(`${URL}/services`),
      axios.get(`${URL}/camerabrands`),
      axios.get(`${URL}/intercomms`),
      axios.get(`${URL}/wiredmasters`),
      axios.get(`${URL}/products`),
      axios.get(`${URL}/cameratypes`),
    ]);

    const servicesData = normalize(servicesRes);
    const brandsData = normalize(brandsRes);
    const intercomsData = normalize(intercomRes);
    const wiredData = normalize(wiredRes);
    const productsData = normalize(productsRes);
    const cameraTypesData = normalize(cameraTypesRes);

    const formattedData = productsData.map((item) => {
      const service = servicesData.find((s) => s.serviceId === item.fkserviceId);
      const brand = brandsData.find((b) => b.cameraBrandId === item.fkcameraBrandId);
      const intercom = intercomsData.find((i) => i.intercommId === item.fkintercommId);
      const wired = wiredData.find((w) => w.wiredId === item.fkwiredId);
      const cameraType = cameraTypesData.find((c) => c.CameraTypeId === item.fkcameraTypeId);

      return {
        ...item,
        serviceCombined: [
          service?.serviceName,
          brand?.cameraBrandName,
          cameraType?.CameraTypeName,
          intercom?.intercommName,
          wired?.wiredName,
        ].filter(Boolean).join(" | "),
        productAmount: item.productAmount ?? null,
        productDiscount: item.productDiscount ?? null,       
        productFinalPrice: item.productFinalPrice ?? null,   
        productImage: item.productImg ? `${URL}${item.productImg}` : "",
        productPdfUrl: item.productPdf ? `${URL}${item.productPdf}` : "",
      };
    });

    setServices(servicesData);
    setBrands(brandsData);
    setIntercoms(intercomsData);
    setWireds(wiredData);
    setCameraTypes(cameraTypesData);
    setDataSource(formattedData);
  } catch (err) {
    console.error("Error fetching data:", err);
    toast.error("Failed to fetch data");
  } finally {
    setLoading(false);
  }
};



  useEffect(() => {
    fetchAll();
  }, []);

  const handleEdit = (product) => {
  setEditProduct({
    ...product,
    productImgPreview: product.productImg
      ? `${URL}${product.productImg}`
      : null,

    productPdfPreview: product.productPdf
      ? `${URL}${product.productPdf}`
      : null,
  });

  form.setFieldsValue(product);
  setModalVisible(true);
};


  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${URL}/products/${id}`);
      toast.success("Product deleted successfully!");
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete product");
    }
  };


  const handleOpenImage = (imgUrl) => {
    setSelectedImage(imgUrl);
    setOpenImageModal(true);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setOpenImageModal(false);
  };

 
 const handleDownloadExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Product List");

  worksheet.columns = [
    { header: "Sr No.", key: "productId", width: 10 },
    { header: "Service", key: "serviceCombined", width: 40 },
    { header: "Product Name", key: "productName", width: 30 },
    {
  accessorKey: "productDescription",
  header: "Description",
  size: 250,
  Cell: ({ row }) => (
    <span
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: "1.4em",
      }}
      title={row.original.productDescription}
    >
      {row.original.productDescription}
    </span>
  ),
}
,
    { header: "Amount", key: "productAmount", width: 15 },
    { header: "Discount (%)", key: "productDiscount", width: 15 },       
    { header: "Final Price", key: "productFinalPrice", width: 15 },    
  ];

  dataSource.forEach((item) => {
    worksheet.addRow({
      productId: item.productId,
      serviceCombined: item.serviceCombined,
      productName: item.productName,
      productDescription: item.productDescription,
      productAmount: item.productAmount ?? "-",
      productDiscount: item.productDiscount ?? "-",       
      productFinalPrice: item.productFinalPrice ?? "-",      
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "Product_List.xlsx");
};

const handleDownloadPDF = () => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(18);
  doc.text("Product List", 40, 40);

  const head = [
    ["Sr No.", "Service", "Product Name", "Description", "Amount", "Discount (%)", "Final Price"]
  ];


  const body = dataSource.map((item) => [
    item.productId,
    item.serviceCombined,
    item.productName,
    item.productDescription,
    item.productAmount ?? "-",
    item.productDiscount ?? "-",
    item.productFinalPrice ?? "-"
  ]);

  autoTable(doc, {
    startY: 60,
    head,
    body,
    headStyles: { fillColor: [23, 66, 83], textColor: "#fff" },
    styles: { fontSize: 10, cellPadding: 6 },
    theme: "grid",
  });

  doc.save("Product_List.pdf");
};

const handlePrint = () => {
  const tableRows = dataSource
    .map(
      (item) => `
      <tr>
        <td>${item.productId}</td>
        <td>${item.serviceCombined}</td>
        <td>${item.productName}</td>
        <td>${item.productDescription}</td>
        <td>${item.productAmount ?? "-"}</td>
        <td>${item.productDiscount ?? "-"}</td>
        <td>${item.productFinalPrice ?? "-"}</td>
      </tr>
    `
    )
    .join("");

  const style = `
    <style>
      @media print {
        @page { size: landscape; margin: 18mm; }
        body { -webkit-print-color-adjust: exact !important; }
      }
      table { width: 100%; border-collapse: collapse; font-size: 14px; }
      th { background: #174253; color: #fff; padding: 10px; text-align: left; border-bottom: 2px solid #174253; }
      td { padding: 10px; border-top: 1px solid #dcdcdc; border-bottom: 1px solid #dcdcdc; }
      tr:nth-child(even) td { background: #f5f5f5; }
      h1 { text-align: center; color: #174253; margin-bottom: 20px; font-size: 22px; }
    </style>`;

  const html = `
    <html>
      <head><title>Product List</title>${style}</head>
      <body>
        <h1>Product List</h1>
        <table>
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Service</th>
              <th>Product Name</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Discount (%)</th>
              <th>Final Price</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>`;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
};


  const columns = useMemo(() => [
    {
      accessorKey: "actions",
      header: "Actions",
      size: 100,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon sx={{ color: "#174253" }} fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.original.productId)}>
            <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { accessorKey: "productId", header: "ID", size: 60 },
    { accessorKey: "serviceCombined", header: "Type", size: 250 },
    { accessorKey: "productName", header: "Product Name", size: 150 },
    {
      accessorKey: "productDescription",
      header: "Description",
      size: 250,
      Cell: ({ row }) => (
        <span
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.4em",
          }}
          title={row.original.productDescription}
        >
          {row.original.productDescription}
        </span>
      ),
    },
    {
    accessorKey: "productAmount",
    header: "Amount",
    size: 120,
    Cell: ({ row }) => (
      <span style={{ fontWeight: 600, color: "#174253" }}>
        ₹ {row.original.productAmount ?? "-"}
      </span>
    ),
  },
   {
    accessorKey: "productDiscount",
    header: "Discount (%)",
    size: 120,
    Cell: ({ row }) => (
      <span style={{ fontWeight: 600, color: "#174253" }}>
        {row.original.productDiscount ?? "-"}
      </span>
    ),
  },
  {
    accessorKey: "productFinalPrice",
    header: "Final Price",
    size: 120,
    Cell: ({ row }) => (
      <span style={{ fontWeight: 600, color: "#174253" }}>
        ₹ {row.original.productFinalPrice ?? "-"}
      </span>
    ),
  },
    {
      accessorKey: "productImage",
      header: "Image",
      size: 150,
      Cell: ({ row }) =>
        row.original.productImage ? (
          <img
            src={row.original.productImage}
            alt="product"
            style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid #ccc", cursor: "pointer" }}
            onClick={() => handleOpenImage(row.original.productImage)}
            onError={(e) => (e.currentTarget.src = "/fallback-image.png")}
          />
        ) : (
          <span style={{ color: "#888" }}>No Image</span>
        ),
    },
    {
      accessorKey: "productPdfUrl",
      header: "PDF",
      size: 120,
      Cell: ({ row }) =>
        row.original.productPdfUrl ? (
          <a
            href={row.original.productPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#174253", fontWeight: 500, display: "flex", alignItems: "center", gap: 5 }}
          >
            <PictureAsPdfIcon fontSize="small" />
            View PDF
          </a>
        ) : (
          <span style={{ color: "#888" }}>No PDF</span>
        ),
    },
  ], []);

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop: 2, p: { xs: 1, md: 0 }, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pr: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}>
          Product List
        </Box>
        <Button
          sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
            "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
          }}
          onClick={() => {
            setEditProduct(null);
            setModalVisible(true);
          }}
        >
          Add Product
        </Button>
      </Box>

     
      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
          <div style={{ position: "absolute", top: 10, right: 20, left: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tooltip title="Download Excel">
                <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </div>
            <TextField
              size="small"
              placeholder="Search products..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
              sx={{ width: { xs: 200, sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: 25, height: 38, backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
            />
          </div>
        </div>

        <MaterialReactTable
  columns={columns}
  data={
    [...dataSource] 
      .reverse()  
      .filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      )
  }

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false}

  muiTablePaperProps={{
    sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

  muiTableBodyRowProps={{
    sx: {
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#eff6fe" },
      "& td": {
        fontSize: { xs: "12px", sm: "14px" },
        p: "8px",
        borderBottom: "0.5px solid #e3e5eb",
        borderRight: "none",
        borderLeft: "none",
        height: "54px",
      },
    },
  }}

  initialState={{
    pagination: {
      pageIndex: 0,
      pageSize: 100, 
    },
  }}
/>

      </Box>

      <Modal
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={modalVisible}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", p: 0, m: 0, borderRadius: 0, boxShadow: "none", overflowY: "auto", outline: "none", bgcolor: "transparent", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <AddProductModal
              product={editProduct}
              onClose={() => setModalVisible(false)}
              refresh={fetchAll}
            />
          </Paper>
        </Fade>
      </Modal>

      <Modal
        open={openImageModal}
        onClose={handleCloseImage}
        closeAfterTransition
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Fade in={openImageModal}>
          <Paper sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "transparent", outline: "none" }}>
            <Box sx={{ position: "relative" }}>
              <IconButton
  onClick={handleCloseImage}
 sx={{ position:"absolute", top:8, right:8, color:"#000", zIndex:10 }}
>
  <CloseIcon />
</IconButton>

              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="preview"
                  style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }}
                />
              )}
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ProductList;
